import React, { useMemo, useState } from 'react';
import { Loader2, MoveRight } from 'lucide-react';
import type { ProjectFile, ProjectFileStream } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import type { MoveVersionsTarget } from '../../utils/projectFileService';
import { structureSelectionLabel } from '../../utils/structureSelectionLabel';
import { groupStreamsByStructure, streamDisplayTitle } from '../../utils/projectFileStreamGroups';
import { trackKindShort as streamKindLabelShort } from './projectUploadModalShared';
import StructureCheckDropdown from './StructureCheckDropdown';
import './ProjectFiles.css';
import './SplitTracksModal.css';

export interface MoveVersionsModalProps {
  sourceStream: ProjectFileStream;
  versions: ProjectFile[];
  /** Other tracks on the project (same kind are offered as destinations). */
  streams: ProjectFileStream[];
  structures: StructureRef[];
  structuresReady: boolean;
  /** Version ids currently live on the client portal (warn before moving). */
  portalHeadFileIds: ReadonlySet<string>;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (target: MoveVersionsTarget) => void;
}

const MoveVersionsModal: React.FC<MoveVersionsModalProps> = ({
  sourceStream,
  versions,
  streams,
  structures,
  structuresReady,
  portalHeadFileIds,
  busy = false,
  onCancel,
  onConfirm,
}) => {
  const destinations = useMemo(
    () =>
      streams.filter(
        s => s.id && s.id !== sourceStream.id && s.streamKind === sourceStream.streamKind && !s.wholeProjectRoot,
      ),
    [streams, sourceStream],
  );
  // Group destinations by structure so "move to Barn" is a one-glance pick.
  const destinationGroups = useMemo(() => groupStreamsByStructure(destinations, structures), [destinations, structures]);
  const [mode, setMode] = useState<'existing' | 'new'>(destinations.length > 0 ? 'existing' : 'new');
  const [destId, setDestId] = useState<string>(destinations[0]?.id ?? '');
  const [newTitle, setNewTitle] = useState('');
  const [newStructureIds, setNewStructureIds] = useState<string[]>([]);

  const sorted = useMemo(
    () => [...versions].sort((a, b) => a.uploadedAt.getTime() - b.uploadedAt.getTime()),
    [versions],
  );
  const movingPortalHead = sorted.some(v => v.id && portalHeadFileIds.has(v.id));
  const movingHead = sorted.some(v => v.isHeadVersion);

  // New-track default: "{structures} — {source title}" once structures are picked, else the source title.
  const autoTitle = useMemo(() => {
    const names = newStructureIds.map(id => structures.find(s => s.id === id)?.name ?? '').filter(Boolean);
    return names.length ? `${structureSelectionLabel(names)} — ${sourceStream.title}` : sourceStream.title;
  }, [newStructureIds, structures, sourceStream.title]);

  const canConfirm =
    !busy &&
    sorted.length > 0 &&
    (mode === 'existing' ? !!destId : (newTitle.trim() || autoTitle).trim().length > 0);

  const confirm = () => {
    if (mode === 'existing') onConfirm({ streamId: destId });
    else
      onConfirm({
        newTrack: {
          title: newTitle.trim() || autoTitle,
          structures: newStructureIds
            .map(id => structures.find(s => s.id === id))
            .filter((s): s is StructureRef => !!s),
        },
      });
  };

  return (
    <div className="pf-modal-overlay" role="presentation" onMouseDown={e => e.target === e.currentTarget && !busy && onCancel()}>
      <div className="pf-modal pf-modal--move" role="dialog" aria-modal="true" aria-labelledby="pf-move-title">
        <h3 className="pf-modal-title" id="pf-move-title">
          <MoveRight size={16} aria-hidden /> Move {sorted.length} version{sorted.length !== 1 ? 's' : ''} to another track
        </h3>
        <p className="pf-modal-hint">
          Versions keep their label, note, LOD and upload date and are appended to the destination in upload
          order. Anything already released to the client or suppliers stays on record exactly as it was sent.
        </p>

        <div className="pf-move-summary">
          From <strong>{sourceStream.title}</strong> ({streamKindLabelShort(sourceStream.streamKind)}):
          <ul>
            {sorted.map(v => (
              <li key={v.id}>
                {v.versionLabel || 'Version'} #{v.versionIndex ?? '?'}
                {v.isHeadVersion ? ' · latest' : ''}
                {v.id && portalHeadFileIds.has(v.id) ? ' · on client portal' : ''}
              </li>
            ))}
          </ul>
          {movingPortalHead ? (
            <p className="pf-move-warn">
              One of these is the version the client currently sees. The portal keeps showing it; it will just be
              listed under the destination track from now on.
            </p>
          ) : null}
          {movingHead && !movingPortalHead ? (
            <p className="pf-move-warn">You are moving the latest version — the previous one becomes latest here.</p>
          ) : null}
        </div>

        <div className="pf-move-choice" role="radiogroup" aria-label="Destination">
          <label>
            <input
              type="radio"
              name="pf-move-mode"
              checked={mode === 'existing'}
              disabled={busy || destinations.length === 0}
              onChange={() => setMode('existing')}
            />
            Existing track
            {destinations.length === 0 ? <span className="pf-split-track-meta"> — none of the same type yet</span> : null}
          </label>
          {mode === 'existing' ? (
            <div className="pf-move-fields">
              <label className="pf-split-field pf-split-field--grow">
                <span className="pf-vr-mu">Track</span>
                <select
                  className="pf-tag-select pf-text-input-height"
                  value={destId}
                  disabled={busy}
                  onChange={e => setDestId(e.target.value)}
                >
                  {destinationGroups.map(g => (
                    <optgroup key={g.structureId} label={g.structureName}>
                      {g.streams.map(s => (
                        <option key={s.id} value={s.id}>
                          {streamDisplayTitle(s, g.structureName)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          <label>
            <input type="radio" name="pf-move-mode" checked={mode === 'new'} disabled={busy} onChange={() => setMode('new')} />
            New track
          </label>
          {mode === 'new' ? (
            <div className="pf-move-fields">
              {structuresReady ? (
                <div className="pf-split-field">
                  <span className="pf-vr-mu">Structures</span>
                  <StructureCheckDropdown
                    compact
                    aria-label="Structures the new track covers"
                    structures={structures}
                    value={newStructureIds}
                    disabled={busy}
                    onChange={setNewStructureIds}
                  />
                </div>
              ) : null}
              <label className="pf-split-field pf-split-field--grow">
                <span className="pf-vr-mu">Track name *</span>
                <input
                  type="text"
                  className="pf-text-input pf-text-input-height"
                  placeholder={autoTitle}
                  value={newTitle}
                  disabled={busy}
                  onChange={e => setNewTitle(e.target.value)}
                />
              </label>
            </div>
          ) : null}
        </div>

        <div className="pf-modal-actions">
          <button type="button" className="pf-modal-cancel" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canConfirm} onClick={confirm}>
            {busy ? (
              <>
                <Loader2 size={14} className="pf-spin" /> Moving…
              </>
            ) : (
              <>
                <MoveRight size={14} aria-hidden /> Move
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveVersionsModal;
